import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, ArrowUpRight, Mail } from "lucide-react";
import { api, fileUrl } from "@/api";
import PageHero from "@/components/PageHero";
import Section from "@/components/Section";
import Reveal from "@/components/Reveal";
import Empty from "@/components/Empty";

function formatDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

export default function Media() {
  const [items, setItems] = useState([]);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    api.get("/media").then((r) => setItems(r.data)).catch(() => {});
    api.get("/company").then((r) => setCompany(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="media-page">
      <PageHero
        eyebrow="Media & Press"
        title="RCS in the media."
        lede="Press releases, news coverage, interviews, awards and announcements from Roy Career Solutions Private Limited."
      />

      <section className="py-24 lg:py-32" data-testid="media-list">
        <div className="container-rcs">
          {items.length === 0 ? (
            <Empty
              icon={FileText}
              title="Press resources are being prepared"
              text="Press releases, media coverage and the RCS media kit will be published here. For media enquiries in the meantime, please reach out below."
              testid="media-empty"
            />
          ) : (
            <div className="space-y-5">
              {items.map((m, i) => (
                <Reveal key={m.id} delay={i * 0.06}>
                  <article className="card-premium flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between" data-testid={`media-item-${i}`}>
                    <div>
                      <span className="eyebrow">{m.type}</span>
                      <h3 className="mt-3 font-serif text-xl text-navy-900">{m.title}</h3>
                      {m.description && <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600">{m.description}</p>}
                      <p className="mt-3 text-xs text-slate-500">{formatDate(m.date)}</p>
                    </div>
                    {m.url && (
                      <a href={fileUrl(m.url)} target="_blank" rel="noopener noreferrer" className="btn-outline-dark shrink-0 !px-6 !py-2.5 !text-xs" data-testid={`media-link-${i}`}>
                        View <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
                      </a>
                    )}
                  </article>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-navy-950 py-20" data-testid="media-enquiries">
        <div className="container-rcs flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
          <Section
            dark
            eyebrow="Media Enquiries"
            title="Working on a story about RCS?"
            lede={`For press, interviews and media kit requests, contact us${company?.email ? ` at ${company.email}` : " through our contact page"} and our team will respond.`}
          />
          <Reveal delay={0.15} className="shrink-0">
            <Link to="/contact" className="btn-gold" data-testid="media-contact-cta">
              <Mail className="h-4 w-4" aria-hidden /> Contact Media Team
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
